// /src/components/UserAuthCopyManager.js

import React, { useEffect, useState } from 'react';
import axios from 'axios';
import DataGrid, { Column, Selection, Scrolling } from 'devextreme-react/data-grid';
import { Button } from 'devextreme-react/button';
import 'devextreme/dist/css/dx.material.blue.light.css';
import API_URL from '../config'; // 중앙 API 설정 파일

export default function UserAuthCopyManager() {
  const [allUsers, setAllUsers] = useState([]);
  const [sourceUserMenus, setSourceUserMenus] = useState([]);

  const [targetUser, setTargetUser] = useState(null);
  const [sourceUser, setSourceUser] = useState(null);

  const [loading, setLoading] = useState(false);

  // 1. 컴포넌트 시작 시 전체 사용자 목록 조회
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/oracle/users`);
        setAllUsers(res.data);
      } catch (err) {
        alert('전체 사용자 목록을 불러오는 데 실패했습니다.');
        console.error(err);
      }
    };
    fetchUsers();
  }, []);

  // 2. 소스 사용자가 선택될 때마다 해당 사용자의 메뉴 목록 조회
  useEffect(() => {
    const fetchSourceUserMenus = async () => {
      if (!sourceUser) {
        setSourceUserMenus([]);
        return;
      }
      try {
        const res = await axios.get(`${API_URL}/api/oracle/usermenu/${sourceUser.USER_ID}`);
        setSourceUserMenus(res.data);
      } catch (err) {
        alert('소스 사용자의 메뉴 목록을 불러오는 데 실패했습니다.');
        setSourceUserMenus([]);
        console.error(err);
      }
    };

    fetchSourceUserMenus();
  }, [sourceUser]);

  // 3. 왼쪽 그리드(대상) 선택 핸들러
  const handleTargetUserSelection = (e) => {
    setTargetUser(e.selectedRowsData.length > 0 ? e.selectedRowsData[0] : null);
  };

  // 4. 오른쪽 그리드(소스) 선택 핸들러
  const handleSourceUserSelection = (e) => {
    setSourceUser(e.selectedRowsData.length > 0 ? e.selectedRowsData[0] : null);
  };

  // 5. 권한 복사 실행 핸들러
  const handleCopyPermissions = async () => {
    if (!targetUser || !sourceUser) {
      alert('대상 사용자와 소스 사용자를 모두 선택해야 합니다.');
      return;
    }

    if (targetUser.USER_ID === sourceUser.USER_ID) {
        alert('자기 자신에게 권한을 복사할 수 없습니다.');
        return;
    }

    const isConfirmed = window.confirm(
      `정말로 '${sourceUser.USER_NAME}' 사용자의 권한을 '${targetUser.USER_NAME}' 사용자에게 복사(덮어쓰기) 하시겠습니까?\n\n대상 사용자의 기존 권한은 모두 삭제됩니다.`
    );

    if (isConfirmed) {
      setLoading(true);
      try {
        await axios.post(`${API_URL}/api/oracle/user-permissions/copy`, {
          targetUserId: targetUser.USER_ID,
          sourceUserId: sourceUser.USER_ID,
        });
        alert('권한 복사가 성공적으로 완료되었습니다.');
      } catch (err) {
        const errorMsg = err.response?.data?.message || '권한 복사 중 오류가 발생했습니다.';
        alert(errorMsg);
        console.error(err);
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <div style={{ padding: '20px', fontFamily: 'sans-serif' }}>
      <h2>사용자별 메뉴 권한 복사</h2>
      <div style={{ display: 'flex', gap: '20px', alignItems: 'flex-start' }}>

        {/* 왼쪽: 대상 사용자 선택 */}
        <div style={{ flex: 1 }}>
          <h3>1. 대상 사용자 선택 (권한을 복사받을 사용자)</h3>
          <DataGrid
            dataSource={allUsers}
            keyExpr="USER_ID"
            height={500}
            selection={{ mode: 'single' }}
            onSelectionChanged={handleTargetUserSelection}
            showBorders
            columnAutoWidth
          >
            <Scrolling mode="virtual" />
            <Selection mode="single" />
            <Column dataField="USER_ID" caption="사용자 ID" width={120} />
            <Column dataField="USER_NAME" caption="사용자명" />
          </DataGrid>
        </div>

        {/* 가운데: 복사 실행 버튼 */}
        <div style={{ textAlign: 'center', paddingTop: '200px' }}>
          <Button
            icon="arrowright"
            text="권한 복사 실행"
            type="success"
            height={50}
            width={180}
            stylingMode="contained"
            onClick={handleCopyPermissions}
            disabled={!targetUser || !sourceUser || loading}
          />
        </div>

        {/* 오른쪽: 소스 사용자 선택 및 메뉴 확인 */}
        <div style={{ flex: 1.5, display: 'flex', flexDirection: 'column', gap: '20px' }}>
            <div>
                <h3>2. 소스 사용자 선택 (권한을 복사할 사용자)</h3>
                <DataGrid
                    dataSource={allUsers}
                    keyExpr="USER_ID"
                    height={230}
                    selection={{ mode: 'single' }}
                    onSelectionChanged={handleSourceUserSelection}
                    showBorders
                    columnAutoWidth
                >
                    <Scrolling mode="virtual" />
                    <Selection mode="single" />
                    <Column dataField="USER_ID" caption="사용자 ID" width={120} />
                    <Column dataField="USER_NAME" caption="사용자명" />
                </DataGrid>
            </div>
            <div>
                <h3>소스 사용자 보유 메뉴 (미리보기)</h3>
                 <DataGrid
                    dataSource={sourceUserMenus}
                    keyExpr="MENU_ID"
                    height={230}
                    showBorders
                    columnAutoWidth
                    noDataText={sourceUser ? "이 사용자는 할당된 메뉴가 없습니다." : "소스 사용자를 선택하세요."}
                >
                    <Scrolling mode="virtual" />
                    <Column dataField="MENU_NAME" caption="메뉴명" />
                    <Column dataField="MENU_URL" caption="URL" />
                </DataGrid>
            </div>
        </div>

      </div>
    </div>
  );
}